import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, ChevronLeft } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import CategoryCard from '../components/ui/CategoryCard';
import ProductCard from '../components/ui/ProductCard';
import { useQuery } from 'react-query';
import { categoriesAPI, productsAPI } from '../services/api';

const CategoriesPage = () => {
  const { t } = useLanguage();
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedCategory, setSelectedCategory] = useState(null);
  const prevCategoryIdRef = useRef(null);
  const limit = 20;
  
  // Get page from URL search params, default to 1, ensure it's a valid positive number
  const pageParam = searchParams.get('page');
  const page = pageParam ? Math.max(1, parseInt(pageParam, 10) || 1) : 1;

  // Fetch categories
  const { data: categoriesData, isLoading: categoriesLoading } = useQuery(
    ['categories'],
    async () => {
      const response = await categoriesAPI.getAll();
      return response.data.categories || [];
    }
  );

  // Fetch products for selected category with pagination
  const { data: productsData, isLoading: productsLoading } = useQuery(
    ['products', { category: id, page, limit }],
    async () => {
      if (!id) return { products: [], pagination: { current_page: 1, total_pages: 1, total_items: 0, items_per_page: limit } };
      const response = await productsAPI.getByCategory(id, { page, limit });
      return response.data;
    },
    {
      enabled: !!id,
      keepPreviousData: true,
      retry: (count, err) => (err?.status === 429 ? count < 0 : count < 2),
      refetchOnWindowFocus: false
    }
  );

  const categories = categoriesData || [];
  const products = productsData?.products || [];
  const pagination = productsData?.pagination;

  // Function to update page in URL
  const updatePage = (newPage) => {
    // Ensure newPage is a valid number
    const validPage = Math.max(1, Math.floor(newPage));
    setSearchParams((prevParams) => {
      const newSearchParams = new URLSearchParams(prevParams);
      if (validPage === 1) {
        newSearchParams.delete('page');
      } else {
        newSearchParams.set('page', validPage.toString());
      }
      return newSearchParams;
    }, { replace: false }); // Use replace: false to create history entries for back button
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  useEffect(() => {
    if (id && categories.length > 0) {
      const categoryId = parseInt(id);
      const category = categories.find(cat => cat.id === categoryId);
      setSelectedCategory(category);
      
      // Only reset page when category ID actually changes, not on initial load
      if (prevCategoryIdRef.current !== null && prevCategoryIdRef.current !== categoryId) {
        // Category changed - reset to first page
        setSearchParams((prevParams) => {
          const newParams = new URLSearchParams(prevParams);
          newParams.delete('page');
          return newParams;
        }, { replace: true });
      }
      
      prevCategoryIdRef.current = categoryId;
    } else if (!id) {
      prevCategoryIdRef.current = null;
    }
  }, [id, categories, setSearchParams]);

  if (id && selectedCategory) {
    // Show products for specific category
    return (
      <div className="min-h-screen bg-gray-900 pt-24 pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Breadcrumb */}
          <div className="flex items-center space-x-2 text-gray-400 mb-8">
            <Link to="/categories" className="hover:text-yellow-500 transition-colors">
              {t('nav.categories')}
            </Link>
            <ChevronLeft size={16} className="rotate-180" />
            <span className="text-white">{selectedCategory.name_fr}</span>
          </div>

          {/* Category Header */}
          <div className="text-center mb-16">
            <h1 className="text-4xl font-bold text-white mb-4">
              {selectedCategory.name_fr}
            </h1>
            <p className="text-gray-300 text-lg max-w-2xl mx-auto">
              {selectedCategory.description_fr}
            </p>
            <div className="mt-4 text-yellow-500 font-semibold">
              {(pagination?.total_items ?? products.length)} produit{(pagination?.total_items ?? products.length) > 1 ? 's' : ''} disponible{(pagination?.total_items ?? products.length) > 1 ? 's' : ''}
            </div>
          </div>

          {/* Products Grid */}
          {productsLoading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500"></div>
            </div>
          ) : products.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-400 text-lg">Aucun produit disponible dans cette catégorie</p>
              <Link
                to="/categories"
                className="inline-flex items-center space-x-2 bg-yellow-500 hover:bg-yellow-600 text-gray-900 font-semibold px-6 py-3 rounded-lg transition-colors duration-300 mt-4"
              >
                <span>Voir toutes les catégories</span>
                <ArrowRight size={18} />
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {products.map((product, index) => (
                <motion.div
                  key={product.id}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className={`
                    ${index % 4 !== 3 ? 'border-r border-[rgb(128,128,128)]' : ''} 
                    ${index < products.length - 4 ? 'border-b border-[rgb(128,128,128)]' : ''}
                  `}
                >
                  <ProductCard product={product} />
                </motion.div>
              ))}
            </div>
          )}

          {/* Pagination controls */}
          {pagination && pagination.total_pages > 1 && (
            <div className="flex items-center justify-center gap-4 mt-10">
              <button
                onClick={() => { if (page > 1) { updatePage(page - 1); } }}
                disabled={page === 1 || productsLoading}
                className={`px-4 py-2 rounded-lg border ${page === 1 ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-800'} text-white border-gray-700`}
              >
                Précédent
              </button>
              <span className="text-gray-300">Page {pagination.current_page} / {pagination.total_pages}</span>
              <button
                onClick={() => { if (page < pagination.total_pages) { updatePage(page + 1); } }}
                disabled={page >= pagination.total_pages || productsLoading}
                className={`px-4 py-2 rounded-lg border ${page >= pagination.total_pages ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-800'} text-white border-gray-700`}
              >
                Suivant
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Show all categories
  return (
    <div className="min-h-screen bg-gray-900 pt-24 pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h1 className="text-4xl font-bold text-white mb-4">
            {t('nav.categories')}
          </h1>
        </div>
        
        {categoriesLoading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500"></div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
            {categories.map((category, index) => (
              <motion.div
                key={category.id}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                 className={`
                   ${index % 3 !== 2 ? 'border-r border-[rgb(128,128,128)]' : ''} 
                   ${index < categories.length - 3 ? 'border-b border-[rgb(128,128,128)]' : ''}
                 `}
              >
                <CategoryCard category={category} />
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CategoriesPage; 